import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { applyThemeVars, buildForm, buildHeader, createHost, resolvePopupStyle, SHARED_PANEL_STYLE, type LeadFormTheme, type RenderHandle } from './shared';

const STYLE = `
  ${SHARED_PANEL_STYLE}
  .panel { width: 100%; box-shadow: none; }
`;

/** The DOM hook a site owner adds to their own page markup for an IN_CONTENT form, e.g.
 * `<div data-cms-lf="newsletter-inline"></div>`. Exported so index.ts can check for it before even
 * arming this form's trigger — no point listening for a trigger to show a form inline when there's
 * nowhere on the current page to put it. */
export function placeholderFor(slug: string): Element | null {
  return document.querySelector(`[data-cms-lf="${slug}"]`);
}

/** Mounts directly into the host page's own placeholder element instead of a floating overlay —
 * the one display type that's part of the page's actual layout. Re-checks `placeholderFor` itself
 * (defense in depth) so it degrades to a no-op handle if ever called without index.ts's own
 * pre-check having found one. */
export function renderInContent(
  form: LeadFormPublicConfig,
  client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
): RenderHandle {
  const mountPoint = placeholderFor(form.slug);
  if (!mountPoint) {
    return { close: () => {} };
  }

  const theme = form.theme as LeadFormTheme;
  const resolved = resolvePopupStyle(theme);
  const { host, root } = createHost(STYLE, mountPoint);

  const panel = document.createElement('div');
  panel.className = 'panel';
  applyThemeVars(panel, resolved);

  for (const el of buildHeader(form, client, panel)) {
    panel.appendChild(el);
  }
  // A fields-less form (a pure "you might also like" recommendations card, no lead capture) has
  // nothing for a submit button to do — skip it rather than rendering a "Submit" that posts an
  // empty payload. Matches the dashboard's own CampaignPreview.tsx, which hides it the same way.
  if (form.fields.length > 0) {
    panel.appendChild(buildForm(form.fields, theme.ctaLabel || 'Submit', onSubmit, () => {
      panel.style.display = 'none';
    }));
  }

  root.appendChild(panel);

  function close() {
    host.remove();
  }

  return { close };
}
